// What an operator has to do about a booking's bid — extracted from
// BookingDetail.tsx so the rules are tested without mounting the page (same
// idiom as vendor-fixed-price-state.ts).
//
// `bid_status = 'ready'` means "the window closed, an operator must act", and
// it carries two different jobs (bookings.bid_ready_reason, migration 0226):
//
//   pick_bid          manual-approve mode — choose the winning bid.
//   match_advertised  a fixed-price partner sale that could not assign itself —
//                     match a crew AT the advertised price, never above it.
//
// Before this module the page treated only `pending` as open work, so a parked
// booking lost its match tools and showed a bare Confirm that strands it.

import type { AdminBookingDetail } from '@/lib/admin-api';

export type BidReadyReason = 'pick_bid' | 'match_advertised';

export interface BidWork {
  /** The match tools (bids list + set-bid card) should be on screen. */
  open: boolean;
  /** Parked for an operator at `ready`, and why. Null while merely pending. */
  parkedReason: BidReadyReason | null;
  /** The advertised price is a CEILING: the customer was sold a firm price and
   *  is never asked for more, so a base above it must not be used. */
  advertisedIsCeiling: boolean;
}

type BidFields = Pick<AdminBookingDetail, 'status' | 'bidStatus'> & {
  bidReadyReason?: string | null;
  bookingMode?: string | null;
};

export function bidWorkFor(b: BidFields): BidWork {
  const bidStatus = b.bidStatus ?? 'pending';
  const matchable = b.status === 'pending' || b.status === 'confirmed';
  const open = matchable && (bidStatus === 'pending' || bidStatus === 'ready');
  const parkedReason: BidReadyReason | null =
    open && bidStatus === 'ready'
      ? b.bidReadyReason === 'match_advertised'
        ? 'match_advertised'
        : 'pick_bid' // includes rows parked before 0226, which have no reason on file
      : null;
  return {
    open,
    parkedReason,
    advertisedIsCeiling: open && (b.bookingMode === 'fixed' || parkedReason === 'match_advertised'),
  };
}

/** A bare "Confirm" skips the crew match, so it is offered only once the bid is
 *  actually resolved — never while pending, and never while parked at ready. */
export function canConfirmWithoutMatch(b: BidFields): boolean {
  const bidStatus = b.bidStatus ?? 'pending';
  return bidStatus !== 'pending' && bidStatus !== 'ready';
}

/** May this per-day bid be used? Always, unless the advertised price is a
 *  ceiling and the bid is above it. An unknown advertised figure allows the
 *  click — the API enforces the same rule (`fixed_price_above_advertised`). */
export function bidIsUsable(
  work: BidWork,
  bidPerDay: number,
  advertisedPerDay: number | null | undefined,
): boolean {
  if (!work.advertisedIsCeiling || advertisedPerDay == null) return true;
  return bidPerDay <= advertisedPerDay + 0.01;
}

/** What "Use this bid" must send to /match. The API reads a bare `basePrice`
 *  as the CAR's currency, so the EUR-normalised `amount` on its own turned a
 *  35 EUR bid into 35 MAD. Send what the crew typed in the currency they typed
 *  it in; fall back to the EUR figure WITH its currency on legacy rows. */
export function matchInputForBid(
  bid: { hostId: number; amount: number; amountOriginal?: number | null; currency?: string | null },
  ledgerCurrency: string,
): { basePrice: number; hostId: number; currency: string } {
  if (bid.amountOriginal != null && bid.currency) {
    return { basePrice: bid.amountOriginal, hostId: bid.hostId, currency: bid.currency };
  }
  return { basePrice: bid.amount, hostId: bid.hostId, currency: ledgerCurrency };
}
